import {
  buildPayload,
  deepEqual,
  draftToNewPlan,
  type UnitsPreference,
} from "@heraion/shared";

import type { PersistedSnapshot } from "./types";

// «Has the server moved on since this draft's baseline?» (#336), answered on
// CONTENT, not on a server stamp: a stamp is missing after a queued save and
// after a create, and a read made right after a save can come back older.
//
// Two snapshots are the same content when they are equal as the editor holds
// them, or when they build the same write. The second reading is what makes a
// baseline left by a SAVE comparable: it is the editor's own draft, while the
// server's copy comes back through programToDraft, and the two can differ in
// bookkeeping (stored translations, variants) without differing in what the
// program prescribes. The assignees are content: an athlete assigned from
// another device since the baseline makes the two differ, and the athlete
// is asked. They compare as a set, and an empty list is the author alone
// (`callerSub`), the server's rule for programs and plans alike: a create
// sends none and the server stores the author. Left out of the write: the typed emails (the server resolves
// them into `assignedTo` accounts), the author's hat (stamped only on a
// create), and the ids of days and exercises (a day created without one
// gets it from the server, so they are compared by position). A snapshot that does not build compares on the first reading
// only.

const OUTSIDE_CONTENT = ["assignedToEmails", "authoredAs"] as const;

function asWrite(
  s: PersistedSnapshot,
  single: boolean,
  cellUnit: UnitsPreference,
  callerSub: string | null,
): Record<string, unknown> | null {
  let write: Record<string, unknown>;
  if (single) {
    const built = draftToNewPlan(s.draft, { scheduledFor: s.scheduledFor, cellUnit });
    if (!built.ok) return null;
    write = { ...built.payload };
  } else {
    const built = buildPayload(s.draft, cellUnit);
    if (!built.ok) return null;
    write = {
      ...built.payload,
      sessions: built.payload.sessions.map(({ id: _day, slots, ...day }) => ({
        ...day,
        slots: slots.map(({ id: _slot, ...slot }) => slot),
      })),
    };
  }
  for (const k of OUTSIDE_CONTENT) delete write[k];
  const listed = (write.assignedTo as string[] | undefined) ?? [];
  const assignees = listed.length > 0 ? listed : callerSub ? [callerSub] : [];
  write.assignedTo = Array.from(new Set(assignees)).sort();
  return write;
}

export function sameContent(
  a: PersistedSnapshot,
  b: PersistedSnapshot,
  single: boolean,
  cellUnit: UnitsPreference,
  callerSub: string | null = null,
): boolean {
  if (deepEqual(a, b)) return true;
  const wa = asWrite(a, single, cellUnit, callerSub);
  const wb = asWrite(b, single, cellUnit, callerSub);
  return wa !== null && wb !== null && deepEqual(wa, wb);
}
